import type { TRectCoords, TXYPoint, TInDirection, TPolygonCoords } from '@/types/Common';
import { ESensorMode, ESensorModel } from '@/types/Sensor';
import { EINSTALLATIONMODE } from '@/types/Space';
import { gql, useQuery } from '@apollo/client';
import { keyBy } from 'lodash';
import {
  SensorConnectionHealthStatus,
  SensorHealthCodeInfo,
} from '../../../pages/DeviceManagementPage/SensorTable';
import { HiveConnectionHealthStatus } from '../../../pages/DeviceManagementPage/HiveTable';
import type { Capacity } from './types';
import { ConnectionHealthCodeStatusHive } from '@/.generated/graphql';
import { getRectRotation, getFovForModel } from '../utils/utils';

export const GET_SPACE_STATE = gql`
  query StudioGetSpace($ids: [String!]!) {
    floors(ids: $ids) {
      data {
        id: floor_id
        name
        timezone
        installation_status
        floorPlans: floor_plans {
          id: floor_plan_id
          name
          coordinates
          url
        }
        tags {
          name
          id
        }
        sensors {
          id: sensor_id
          name
          center
          mode
          model
          orientation
          isOnline: is_online
          inDirection: in_direction
          clientId: client_id
          connectionHealth: connection_health {
            status
            codes {
              lastTimestamp: last_timestamp
              comparedTimestamp: compared_timestamp
              code
              isError: is_error
              description
            }
          }
          isSleepTime: is_sleep_time
          macAddress: mac_address
          fov: field_of_view
          height
          isEntrance: is_entrance
          doorLine: door_line
          parallelToDoor: parallel_to_door
          hive {
            id: hive_id
            name
            serialNumber: serial_number
            config {
              apiDrivenSensorMode: api_driven_sensor_mode
            }
          }
          algoConfig: algo_config {
            coldPersonDetection: cold_person_detection
            sunlight
            shortenedDoorline: shortened_doorline
            polylineDoorLine: polyline_door_line
          }
          algorithmConfig: algorithm_config
          room {
            name
            id: room_id
            coordinates
            rotation
          }
          lastBatteryChangeDate: last_battery_change_date
          nextBatteryChangeDate: next_battery_change_date
          note
          config {
            use_pir
          }
          powerType: power_type
          isClone: is_clone
          isMirror: is_mirror
          sourceMacAddress: source_mac_address
          estimatedTotalRuntimeDays: estimated_total_runtime_days
          deviceStatus: device_status
          lastNetworkId: last_network_id
        }
        rooms {
          id: room_id
          name
          coordinates
          rotation
          capacity {
            max
            mid
          }
          sensors {
            id: sensor_id
            name
          }
          note
          customID
          tags {
            name
            id
          }
          pir_zero_enable
          pir_zero_threshold
          pir_zero_window
        }
        zones {
          id: zone_id
          name
          coordinates
          rotation
          capacity {
            max
            mid
          }
          note
          customID
          tags {
            name
            id
          }
        }
        hives {
          id: hive_id
          name
          coordinates
          serial_number
          sourceSerialNumber: source_serial_number
          model_name
          networkId: last_network_id
          config {
            frame_rate {
              presence
              traffic
            }
          }
          sensors {
            id: sensor_id
            macAddress: mac_address
          }
          connectionHealth: connection_health {
            status
            codes {
              lastTimestamp: last_timestamp
              comparedTimestamp: compared_timestamp
              code
              isError: is_error
              description
            }
          }
          isSleepTime: is_sleep_time
          isClone: is_clone
          note
          deviceStatus: device_status
          sourceSerialNumber: source_serial_number
        }
      }
    }
  }
`;

/** Client-side only: discriminates desk-occupancy sensors and their co-created spots */
export type DeviceType = 'desk_sensor';

export type Sensor = {
  id: string;
  name: string;
  center: TXYPoint;
  mode: ESensorMode;
  macAddress: string;
  orientation: [number, number, number];
  fov: number;
  height: number;
  isEntrance?: boolean;
  deviceStatus?: 'ACTIVE' | 'ARCHIVED';
  model: ESensorModel;
  doorLine?: number;
  parallelToDoor?: boolean;
  inDirection?: TInDirection;
  clientId?: string;
  connectionHealth?: {
    status: SensorConnectionHealthStatus;
    codes: SensorHealthCodeInfo[];
  };
  room?: {
    name: string;
    id: string;
    coordinates: TRectCoords | TPolygonCoords;
    rotation: number;
  };
  hive?: {
    id: string;
    name: string;
    serialNumber: string;
    sourceSerialNumber?: string;
    config?: {
      apiDrivenSensorMode?: boolean | null;
    } | null;
  };
  algoConfig?: {
    shortenedDoorline?: [number, number];
    coldPersonDetection?: boolean;
    sunlight?: boolean;
    /**
     * Polyline doorline coordinates for traffic sensors. Coordinate system: floor-plan math
     * coords (bottom-left origin, y-up), normalized 0-1 to the sensor's coverage box.
     * Editable via Studio's polygon doorline editor — written through `algo_config.polyline_door_line`.
     */
    polylineDoorLine?: [number, number][];
  };
  /**
   * Raw JSON dump of algorithm config from the database.
   * Known sub-structure: `count.polyline_door_line` is the raw coordinate list (`[[x, y], ...]`)
   * for traffic sensors with polyline doorlines.
   *
   * Coordinate system: floor-plan math coords (bottom-left origin, y-up), normalized 0-1 to the
   * sensor's coverage box. Corners: [0,0]=bottom-left, [1,0]=bottom-right, [1,1]=top-right,
   * [0,1]=top-left.
   */
  algorithmConfig?: {
    count?: {
      polyline_door_line?: [number, number][];
      [key: string]: unknown;
    };
    [key: string]: unknown;
  } | null;
  // Custom scalar is not yet supported in apollo client
  // https://community.apollographql.com/t/custom-scalars-on-the-client-side/6587/3
  lastBatteryChangeDate?: string | null;
  nextBatteryChangeDate?: string | null;
  note?: string;
  /**
   * Mouse event can be blocked by selection box.
   * We need manually set hover state to the item when necessary
   */
  selectedHover?: boolean;
  powerType: 'Wired' | 'Battery';
  estimatedTotalRuntimeDays?: number | null;
  lastNetworkId?: number | null;
  config?: {
    use_pir?: boolean;
  };
  isSleepTime?: boolean;
  isMirror?: boolean;
  isClone?: boolean;
  sourceMacAddress?: string | null;
  /** Set when created via right-click context menu — source is locked (not changeable). */
  sourceLockedByContextMenu?: boolean;
  isArchived?: boolean;
  /** Set when a sensor has been moved to a different floor via the reducer. */
  floorId?: string;
  /** Client-side only: 'desk_sensor' for sensors co-created with a desk spot */
  device_type?: DeviceType;
  /** Client-side only: ID of the spot co-created alongside this desk sensor */
  spot_id?: string;
};

export type Room = {
  id: string;
  name: string;
  coordinates: TRectCoords | TPolygonCoords;
  rotation: number;
  capacity: Capacity;
  sensors: {
    id: string;
    name: string;
  }[];
  note?: string;
  customID?: string;
  tags?: {
    name: string;
    id: string;
  }[];
  pir_zero_enable?: boolean;
  pir_zero_threshold?: number | null;
  pir_zero_window?: number | null;
  /**
   * Mouse event can be blocked by selection box.
   * We need manually set hover state to the item when necessary
   */
  selectedHover?: boolean;
};

export type Zone = {
  id: string;
  name: string;
  room_id?: string;
  room?: Room;
  coordinates: TRectCoords | TPolygonCoords;
  rotation: number;
  capacity: Capacity;
  note?: string;
  customID?: string;
  tags?: {
    name: string;
    id: string;
  }[];
  /**
   * Mouse event can be blocked by selection box.
   * We need manually set hover state to the item when necessary
   */
  selectedHover?: boolean;
};

export type Hive = {
  id: string;
  name: string;
  coordinates: TXYPoint;
  serial_number: string;
  deviceStatus?: 'ACTIVE' | 'ARCHIVED';
  sensors?: {
    id: string;
    macAddress: string;
  }[];
  networkId?: number;
  config?: {
    frame_rate?: {
      presence?: string;
      traffic?: string;
    };
  };
  connectionHealth: {
    status: HiveConnectionHealthStatus;
    // can't use the generated gql types as they have snake_case fields...
    codes: {
      lastTimestamp: number;
      comparedTimestamp: number;
      code: ConnectionHealthCodeStatusHive;
      description: string;
      isError: boolean;
    }[];
  };
  note?: string;
  isSleepTime?: boolean;
  model_name?: string;
  floorId?: string;
  isClone?: boolean;
  sourceSerialNumber?: string;
  sourceHiveId?: string;
  isArchived?: boolean;
};

export type FloorPlan = {
  id: string;
  url: string;
  name: string;
  coordinates: TRectCoords;
  base64?: string;
  aspectRatioLocked: boolean;
  rotation: number;
};

export type Floor = {
  id: string;
  name: string;
  timezone: string;
  sensors: Sensor[];
  rooms: Room[];
  zones: Zone[];
  hives: Hive[];
  floorPlans: FloorPlan[];
  installation_status: EINSTALLATIONMODE;
};

type StateData = {
  floors: {
    data: Floor[];
  };
};

export type Spot = {
  id: string;
  name: string;
  spot_type: string;
  custom_id?: string;
  location: [number, number];
  floor_id: string;
  room_id?: string;
  note?: string;
  tags?: { id: string; name: string }[];
  /** Client-side only: 'desk_sensor' for spots co-created with a desk occupancy sensor */
  device_type?: DeviceType;
};

// device_type values must match what the upstream DT enrichment worker expects.
// The worker receives webhooks from DT Cloud, does ID mapping / aggregation, and
// writes back to Postgres via GraphQL — it uses exactly these two string literals.
export type Sensor3PDeviceType = 'motion_sensor' | 'desk_sensor';

/** Narrows an unknown server string to Sensor3PDeviceType; falls back to 'motion_sensor'. */
export function toSensor3PDeviceType(value: string | null | undefined): Sensor3PDeviceType {
  return value === 'motion_sensor' || value === 'desk_sensor' ? value : 'motion_sensor';
}

export type Sensor3P = {
  id: string;
  name: string;
  provider: string;
  device_type: Sensor3PDeviceType;
  floor_id: string;
  location: [number, number];
  external_device_id?: string | null;
  spot_id?: string | null;
  gateway_3p_id?: string;
  height?: number;
  orientation_x_rad?: number;
  orientation_y_rad?: number;
  room_id?: string;
  note?: string;
  tags?: { id: string; name: string }[];
  provisioned_at?: string;
};

export type Gateway3P = {
  id: string;
  name: string;
  provider: string;
  floor_id: string;
  location: [number, number];
  external_gateway_id?: string | null;
  custom_id?: string;
  room_id?: string;
  note?: string;
  tags?: { id: string; name: string }[];
  provisioned_at?: string;
};

export type Item = Sensor | Room | Zone | Hive | FloorPlan | Spot | Sensor3P | Gateway3P;

export const useGetState = ({ spaceId, skip }: { spaceId?: string; skip?: boolean }) => {
  const { data, ...rest } = useQuery<StateData>(GET_SPACE_STATE, {
    variables: { ids: [spaceId] },
    pollInterval: 1000 * 30,
    skip: skip || !spaceId,
  });

  const floorData = data?.floors?.data[0];

  // populate containing room for each zone if any — clone to avoid mutating Apollo cache
  const allRooms = keyBy(floorData?.rooms ?? ([] as Room[]), 'id');
  const allZones = (floorData?.zones ?? ([] as Zone[])).map((z) => ({ ...z }));
  allZones.forEach((zone) => {
    if (zone.room_id && zone.room_id in allRooms) {
      zone.room = allRooms[zone.room_id];
    }
  });

  return {
    data: floorData
      ? {
          ...floorData,
          // Use cloned zones (with room populated) to avoid mutating Apollo cache
          zones: allZones,
          // Correct FOV for existing sensors based on their model
          sensors:
            floorData?.sensors?.map((sensor) => ({
              ...sensor,
              fov: getFovForModel(sensor.model),
              isClone: !sensor.isMirror && sensor.isClone,
              isArchived: sensor.deviceStatus === 'ARCHIVED',
            })) ?? [],
          hives:
            floorData?.hives?.map((hive) => ({
              ...hive,
              isArchived: hive.deviceStatus === 'ARCHIVED',
              isClone: hive.isClone,
            })) ?? [],
          floorPlans:
            floorData?.floorPlans?.map((fp) => ({
              ...fp,
              // Floor plans don't have true rotation yet, it's all based on the order of the coordinates....
              rotation: getRectRotation(fp.coordinates),
              // Default aspect ratio to locked for new and existing floor plans
              aspectRatioLocked: fp.aspectRatioLocked ?? true,
            })) ?? [],
        }
      : null,
    ...rest,
  };
};
